import { router } from 'expo-router';
import { useEffect, useState } from 'react';
import { NativeScrollEvent, NativeSyntheticEvent, StyleSheet, useWindowDimensions, View } from 'react-native';
import Animated from 'react-native-reanimated';

import { NonProfitOrg, NonProfitOrgType } from '@/api/models/nonProfitOrgModels';
import useOnLightRatingPress from '@/hooks/useOnLightRatingPress';
import { safeJsonParseArray } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';

import ListEmptyComponent from '../ListEmptyComponent';
import NextPageFetchingIndicator from '../NextPageFetchingIndicator';
import NonProfitOrganizationCard from './NonProfitOrganizationCard';

interface NpoListProps {
  nposData: NonProfitOrg[] | undefined;
  onEndReached: () => void;
  refetch: () => void;
  isRefetching: boolean;
  npoType: NonProfitOrgType;
  isFetchingNextPage: boolean;
  userAdministratedNpoId?: string;
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  scrollEnabled?: boolean;
}

const NpoList = ({
  nposData,
  onEndReached,
  refetch,
  isFetchingNextPage,
  isRefetching,
  npoType,
  userAdministratedNpoId,
  onScroll,
  scrollEnabled
}: NpoListProps) => {
  const { width } = useWindowDimensions();

  const [numOfColumns, setNumOfColumns] = useState(3);
  const [npoListWidth, setNpoListWidth] = useState<number>(width);

  const { onLightRatingPress } = useOnLightRatingPress();

  const cardSize = (npoListWidth - (numOfColumns - 1) * style.flatListColumnWrapperStyle.gap) / numOfColumns;

  useEffect(() => {
    if (width > 820 && numOfColumns !== 3) {
      setNumOfColumns(3);
    } else if (width > 640 && width < 820 && numOfColumns !== 2) {
      setNumOfColumns(2);
    } else if (width < 640 && numOfColumns !== 1) {
      setNumOfColumns(1);
    }
  }, [numOfColumns, width]);

  return (
    <View
      ref={(node) => {
        if (node) node.measureInWindow((x, y, w, h) => setNpoListWidth(w));
      }}
      style={style.container}>
      <Animated.FlatList
        //Needed to force refresh of columns on the FlatList
        key={numOfColumns}
        onScroll={onScroll}
        scrollEnabled={scrollEnabled}
        style={style.flatList}
        {...(numOfColumns > 1 && {
          columnWrapperStyle: style.flatListColumnWrapperStyle
        })}
        contentContainerStyle={style.flatListContentContainerStyle}
        horizontal={false}
        numColumns={numOfColumns}
        keyExtractor={(item: NonProfitOrg) => item.id}
        data={nposData}
        showsVerticalScrollIndicator={false}
        onRefresh={refetch}
        refreshing={isRefetching}
        onEndReachedThreshold={0.75}
        onEndReached={onEndReached}
        renderItem={({ item }: { item: NonProfitOrg }) => {
          return (
            <NonProfitOrganizationCard
              npoId={item.id}
              key={item.id}
              size={cardSize}
              title={item.name}
              subtitle={item.description}
              assets={safeJsonParseArray(item.thumbnail ?? '[]')}
              lightRatingValue={parseFloat(item.lightRaitingScore ?? '0')}
              claimed={item.claimed}
              showLightRating={item.claimed}
              onPress={() => {
                if (item.id === userAdministratedNpoId) {
                  router.navigate('/(app)/(bottomTabs)/profile/npoProfile/insights');
                } else {
                  router.navigate({
                    pathname: `/npoDetail/[npoId]/insights`,
                    params: {
                      npoId: item.id,
                      thumbnail: item.thumbnail,
                      name: item.name,
                      lightRating: parseFloat(item.lightRaitingScore ?? '0')
                    }
                  });
                }
              }}
              onLightRatingPress={() =>
                onLightRatingPress({
                  name: item?.name,
                  lightRating: parseFloat(item?.lightRaitingScore ?? '0')
                })
              }
            />
          );
        }}
        ListEmptyComponent={
          <ListEmptyComponent
            label={npoType === 'CHARITIES' ? i18n.t('home.empty_charities') : i18n.t('home.empty_churches')}
          />
        }
        ListFooterComponent={isFetchingNextPage ? <NextPageFetchingIndicator /> : null}
      />
    </View>
  );
};

export default NpoList;

const style = StyleSheet.create({
  container: {
    height: '100%',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center'
  },

  flatList: {
    width: '100%',
    marginTop: 26,
    flex: 1
  },

  //Vertical Gap for flatList
  flatListContentContainerStyle: {
    flex: 1,
    gap: 27
  },

  //Horizontal Gap for flatList
  flatListColumnWrapperStyle: {
    gap: 22,
    justifyContent: 'flex-start'
  }
});
